import { useGetEmployees, useGetEmployeesDriverStats } from '@agos/api-client/hr'
import { useGetVehicles } from '@agos/api-client/orders'
import { formatDecimal, T, toDateParam, tt } from '@agos/shared'
import { EmptyState, PageHeader, StatCard, TabGroup, cn } from '@agos/ui'
import {
  BarChart3,
  ChevronDown,
  ChevronUp,
  MapPin,
  Package,
  Radio,
  Truck,
  User,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  byDeliveries,
  filterDrivers,
  initialsOf,
  isKilometresDerived,
  kilometresOf,
  statsTotals,
  stopsOf,
  type DriverStat,
} from './driver-stats'

/**
 * `/delivery-statistics` — who drove what, ported from legacy
 * `pages/delivery-statistics.tsx`.
 *
 * Two tabs. *Prehľad* is a period: pick dates, a driver, a vehicle, read the
 * totals. *Live* is today, polled every 30 seconds, showing who is out right now.
 *
 * `/drivers` redirects here, which is what makes that redirect land.
 *
 * No charts. Legacy draws this with stat cards and tables and so does the port —
 * recharts is pinned in `docs/DECISIONS.md` for the reporting module, not for
 * this page.
 */

const LIVE_REFRESH_MS = 30_000
const DEFAULT_DAYS = 7

function daysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return toDateParam(date)
}

interface EmployeeLike {
  id?: number
  name?: string
  systemRole?: string
}

interface VehicleLike {
  id?: number
  name?: string
  licensePlate?: string
}

type Tab = 'prehlad' | 'live'

export function DeliveryStatisticsPage() {
  const [tab, setTab] = useState<Tab>('prehlad')
  const [dateFrom, setDateFrom] = useState(() => daysAgo(DEFAULT_DAYS))
  const [dateTo, setDateTo] = useState(() => toDateParam(new Date()))
  const [driverId, setDriverId] = useState<number | null>(null)
  const [vehicleId, setVehicleId] = useState<number | null>(null)

  const { data: employeeData } = useGetEmployees({})
  const { data: vehicleData } = useGetVehicles({})

  const drivers = useMemo(
    () =>
      (Array.isArray(employeeData) ? (employeeData as EmployeeLike[]) : []).filter(
        (employee) => employee.systemRole === 'driver',
      ),
    [employeeData],
  )
  const vehicles = useMemo(
    () => (Array.isArray(vehicleData) ? (vehicleData as VehicleLike[]) : []),
    [vehicleData],
  )

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Štatistiky rozvozu')}
        description={tt('Kto koľko odvezol a kam.')}
        icon={<BarChart3 className="w-5 h-5" />}
      />

      <TabGroup
        tabs={[
          { id: 'prehlad', label: tt('Prehľad'), icon: BarChart3 },
          { id: 'live', label: tt('Live'), icon: Radio },
        ]}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />

      {tab === 'prehlad' ? (
        <OverviewTab
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFrom={setDateFrom}
          onDateTo={setDateTo}
          driverId={driverId}
          onDriverId={setDriverId}
          vehicleId={vehicleId}
          onVehicleId={setVehicleId}
          drivers={drivers}
          vehicles={vehicles}
        />
      ) : (
        <LiveTab />
      )}
    </div>
  )
}

const FILTER_CLASSES =
  'px-3 py-2 rounded-xl bg-card border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20'

function OverviewTab({
  dateFrom,
  dateTo,
  onDateFrom,
  onDateTo,
  driverId,
  onDriverId,
  vehicleId,
  onVehicleId,
  drivers,
  vehicles,
}: {
  dateFrom: string
  dateTo: string
  onDateFrom: (value: string) => void
  onDateTo: (value: string) => void
  driverId: number | null
  onDriverId: (value: number | null) => void
  vehicleId: number | null
  onVehicleId: (value: number | null) => void
  drivers: EmployeeLike[]
  vehicles: VehicleLike[]
}) {
  const { data, isLoading } = useGetEmployeesDriverStats({ from: dateFrom, to: dateTo })

  const stats = useMemo(() => {
    const all = Array.isArray(data) ? (data as DriverStat[]) : []
    // Filtered client-side: the endpoint accepts driverId and vehicleId, but
    // filtering here keeps the totals and the list from ever disagreeing about
    // which set they describe.
    return byDeliveries(filterDrivers(all, { driverId, vehicleId }))
  }, [data, driverId, vehicleId])

  const totals = statsTotals(stats)
  const anyDerived = stats.some(isKilometresDerived)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <LabelledControl label={tt('Od')}>
          <input
            type="date"
            value={dateFrom}
            onChange={(event) => onDateFrom(event.target.value)}
            aria-label={tt('Od')}
            className={FILTER_CLASSES}
          />
        </LabelledControl>
        <LabelledControl label={tt('Do')}>
          <input
            type="date"
            value={dateTo}
            onChange={(event) => onDateTo(event.target.value)}
            aria-label={tt('Do')}
            className={FILTER_CLASSES}
          />
        </LabelledControl>
        <LabelledControl label={tt('Šofér')}>
          <select
            value={driverId === null ? '' : String(driverId)}
            onChange={(event) => onDriverId(event.target.value ? Number(event.target.value) : null)}
            aria-label={tt('Šofér')}
            className={FILTER_CLASSES}
          >
            <option value="">{tt('Všetci šoféri')}</option>
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driver.name}
              </option>
            ))}
          </select>
        </LabelledControl>
        <LabelledControl label={tt('Vozidlo')}>
          <select
            value={vehicleId === null ? '' : String(vehicleId)}
            onChange={(event) => onVehicleId(event.target.value ? Number(event.target.value) : null)}
            aria-label={tt('Vozidlo')}
            className={FILTER_CLASSES}
          >
            <option value="">{tt('Všetky vozidlá')}</option>
            {vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {[vehicle.name, vehicle.licensePlate].filter(Boolean).join(' · ')}
              </option>
            ))}
          </select>
        </LabelledControl>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label={tt('Šoférov')} value={String(totals.driverCount)} icon={User} color="violet" />
        <StatCard
          label={tt('Rozvozov')}
          value={String(totals.deliveries)}
          icon={Package}
          color="blue"
        />
        <StatCard
          label={tt('Kilometrov')}
          value={`${formatDecimal(totals.kilometres, 1)} km`}
          icon={MapPin}
          color="emerald"
        />
        <StatCard
          label={tt('km / rozvoz')}
          value={formatDecimal(totals.kmPerDelivery, 1)}
          icon={Truck}
          color="amber"
        />
      </div>

      {anyDerived && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          {/* FB-060 - shown only when the endpoint sends no total and the addresses carry the real
              distances, so the figure above is summed from the stops. The server-side cause is fixed,
              so this is now the exception rather than every request. */}
          <T>Kilometre sú spočítané zo zastávok - server pre toto obdobie neposiela súčet.</T>
        </p>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((placeholder) => (
            <div
              key={placeholder}
              className="h-16 rounded-2xl bg-muted/40 border border-border animate-pulse"
            />
          ))}
        </div>
      ) : stats.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<BarChart3 className="w-10 h-10" />}
            title={tt('Žiadne rozvozy')}
            description={tt('V tomto období nikto nič neodvážal.')}
          />
        </div>
      ) : (
        <div className="space-y-2">
          {stats.map((driver) => (
            <DriverRow key={driver.id} driver={driver} />
          ))}
        </div>
      )}
    </div>
  )
}

function LiveTab() {
  const today = toDateParam(new Date())
  const { data, isLoading } = useGetEmployeesDriverStats(
    { from: today, to: today },
    { query: { refetchInterval: LIVE_REFRESH_MS } },
  )

  const stats = useMemo(
    () => byDeliveries(Array.isArray(data) ? (data as DriverStat[]) : []),
    [data],
  )
  const totals = statsTotals(stats)
  const available = stats.filter((driver) => driver.isAvailable)

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label={tt('Na zmene')}
          value={String(available.length)}
          icon={Radio}
          color="emerald"
        />
        <StatCard
          label={tt('Rozvozov dnes')}
          value={String(totals.deliveries)}
          icon={Package}
          color="blue"
        />
        <StatCard
          label={tt('Kilometrov dnes')}
          value={`${formatDecimal(totals.kilometres, 1)} km`}
          icon={MapPin}
          color="violet"
        />
        <StatCard
          label={tt('km / rozvoz')}
          value={formatDecimal(totals.kmPerDelivery, 1)}
          icon={Truck}
          color="amber"
        />
      </div>

      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" aria-live="polite">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <T>Obnovuje sa každých 30 sekúnd.</T>
      </p>

      {isLoading ? (
        <div className="h-16 rounded-2xl bg-muted/40 border border-border animate-pulse" />
      ) : stats.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12">
          <EmptyState
            icon={<Radio className="w-10 h-10" />}
            title={tt('Dnes nikto nerozvážal')}
            description={tt('Zatiaľ žiadne rozvozy.')}
          />
        </div>
      ) : (
        <div className="space-y-2">
          {stats.map((driver) => (
            <DriverRow key={driver.id} driver={driver} showAvailability />
          ))}
        </div>
      )}
    </div>
  )
}

function DriverRow({
  driver,
  showAvailability,
}: {
  driver: DriverStat
  showAvailability?: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const stops = stopsOf(driver)
  const kilometres = kilometresOf(driver)

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={`${driver.name ?? ''} - ${tt('zastávky')}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-black shrink-0">
          {initialsOf(driver.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{driver.name}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {[driver.vehicleName, driver.licensePlate].filter(Boolean).join(' · ') || '-'}
          </p>
        </div>

        {showAvailability && (
          <span
            className={cn(
              'text-[10px] font-bold px-2 py-0.5 rounded-full border',
              driver.isAvailable
                ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/25'
                : 'bg-muted text-muted-foreground border-border',
            )}
          >
            {driver.isAvailable ? tt('na zmene') : tt('mimo')}
          </span>
        )}

        <span className="text-right shrink-0">
          <span className="block text-sm font-bold tabular-nums">
            {driver.deliveryCount ?? 0} <T>rozvozov</T>
          </span>
          <span className="block text-[11px] text-muted-foreground tabular-nums">
            {formatDecimal(kilometres, 1)} km · {stops.length} <T>zastávok</T>
          </span>
        </span>

        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        )}
      </button>

      {isOpen && (
        <div className="border-t border-border px-4 py-2.5 space-y-1">
          {stops.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              <T>Žiadne zastávky</T>
            </p>
          ) : (
            stops.map((stop) => (
              <div key={stop.address} data-driver-stop className="flex items-baseline gap-2 text-xs">
                <MapPin className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />
                <span className="flex-1 min-w-0 truncate">{stop.address}</span>
                {stop.visits > 1 && (
                  <span className="text-[10px] font-bold text-primary shrink-0">
                    {stop.visits}×
                  </span>
                )}
                <span className="tabular-nums text-muted-foreground shrink-0">
                  {formatDecimal(stop.km, 2)} km
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function LabelledControl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1">
      <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider block">
        {label}
      </span>
      {children}
    </label>
  )
}
